/**
 * The tool-string HEADER form (name, line type, notes), full-page on New Tool
 * String and as the edit panel on its own page. The items are edited
 * separately (ToolStringItems), because the API replaces them as one list.
 *
 * A blank line type means the string runs on any line (sent as null). On edit
 * only the keys that changed are sent.
 */
import React, { useMemo, useState } from 'react';
import { wlBlankToNull } from '../../lib/wireline-slickline';
import type { WlCatalog, WlToolString, WlToolStringWrite } from '../../types/wireline-slickline';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlField, WlFormError } from './WlBits';

interface HeaderState {
  name: string;
  line_type: string;
  notes: string;
}

const headerState = (t: WlToolString | null): HeaderState => ({ name: t?.name ?? '', line_type: t?.lineType ?? '', notes: t?.notes ?? '' });

export function ToolStringForm({
  toolString,
  catalog,
  pending,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  toolString: WlToolString | null;
  catalog: WlCatalog | null;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (body: WlToolStringWrite) => void;
  onCancel: () => void;
}) {
  const initial = useMemo(() => (toolString ? headerState(toolString) : null), [toolString]);
  const [form, setForm] = useState<HeaderState>(() => headerState(toolString));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof HeaderState>(k: K, v: HeaderState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.name.trim()) return setLocalError('Give the tool string a name, e.g. Standard slickline string.');
    const body: WlToolStringWrite = {};
    const changed = (k: keyof HeaderState) => !initial || form[k] !== initial[k];
    if (changed('name')) body.name = form.name.trim();
    if (changed('line_type')) body.line_type = form.line_type || null;
    if (changed('notes')) body.notes = wlBlankToNull(form.notes);
    if (initial && Object.keys(body).length === 0) return setLocalError('Nothing has changed.');
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="wl-tool-string-form" noValidate>
      <WlFormError message={localError ?? error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <WlField label="Name">
          <input className={WL_INPUT} maxLength={100} value={form.name} onChange={(e) => set('name', e.target.value)} aria-label="Name" />
        </WlField>
        <WlField label="Line type" hint="Blank: runs on any line.">
          <select className={WL_INPUT} value={form.line_type} onChange={(e) => set('line_type', e.target.value)} aria-label="Line type">
            <option value="">Any line</option>
            {(catalog?.lineTypes ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </WlField>
      </div>
      <WlField label="Notes">
        <textarea className={`${WL_INPUT} h-20 py-2`} value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Notes" />
      </WlField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WL_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
